import { useQuery } from '@tanstack/react-query';
import ConfirmModal from 'components/confirm-modal/confirm-modal';
import FieldError from 'components/field-error/field-error';
import Label from 'components/label/label';
import { useFormikContext } from 'formik';
import {
  getPhoneNumberStatusOptions,
  useBuyViewPhoneNumber,
  useReplacePhoneNumber,
} from 'generated/api-client/@tanstack/react-query.gen';
import { useProPhoneNumbersQueryLazyQuery } from 'generated/graphql';
import React, { FunctionComponent, useCallback } from 'react';
import {
  Button,
  FormField,
  Icon,
  Label as LabelUi,
  Popup,
} from 'semantic-ui-react';
import { PhoneNumber, ToastError, ToastSuccess } from 'utils';
import { ProFormUpdateFields } from '../pro-form-update-fields-type';
import ReplacePhoneNumberButton from './replace-phone-number-button';

type ProPhoneNumbersFieldProps = {
  proViewId: number;
};

const ProPhoneNumbersField: FunctionComponent<ProPhoneNumbersFieldProps> = ({
  proViewId,
}) => {
  const [fetchPhoneNumbers] = useProPhoneNumbersQueryLazyQuery();
  const { values, errors, setFieldValue } =
    useFormikContext<ProFormUpdateFields>();

  const { mutateAsync: buyPhoneNumber } = useBuyViewPhoneNumber();
  const { mutateAsync: replacePhoneNumber } = useReplacePhoneNumber();

  const refetchPhoneNumbers = useCallback(async () => {
    const { data } = await fetchPhoneNumbers({
      variables: { proViewId },
      fetchPolicy: 'network-only',
    });

    const phoneNumbers =
      data?.phoneNumber?.map((phoneNumber) => ({
        id: phoneNumber.id,
        number: phoneNumber.number,
        tag: phoneNumber.tag,
        status: 'unverified',
      })) || [];

    setFieldValue('phoneNumbers', phoneNumbers);

    return phoneNumbers;
  }, [fetchPhoneNumbers, proViewId, setFieldValue]);

  const webPhoneNumber = values.phoneNumbers.find(({ tag }) => tag === 'web');
  const guidePhoneNumber = values.phoneNumbers.find(
    ({ tag }) => tag === 'guide',
  );
  const releasedPhoneNumbers = values.phoneNumbers.filter(
    ({ tag }) => tag === 'released',
  );
  const otherPhoneNumbers = values.phoneNumbers.filter(
    ({ tag }) => tag !== 'web' && tag !== 'guide' && tag !== 'released',
  );

  const renderPhoneNumberStatusLabel = (phoneNumberStatus: string) => {
    if (phoneNumberStatus === 'unverified') {
      return null;
    }

    const statusText =
      phoneNumberStatus === 'active' ? 'Actif' : 'En cours de vérification';
    const statusColor = phoneNumberStatus === 'active' ? 'green' : 'orange';

    return (
      <LabelUi size="big" color={statusColor}>
        {statusText}
      </LabelUi>
    );
  };

  const onBuyProNumber = useCallback(async (): Promise<void> => {
    if (!values.proPresentation.companyId) {
      ToastError(
        'Erreur',
        "Vous devez d'abord ajouter une entreprise avant de pouvoir générer un numéro",
      );
      return;
    }

    try {
      await buyPhoneNumber({
        path: { viewId: proViewId },
      });

      await refetchPhoneNumbers();
      ToastSuccess('Succès', 'Numéro de téléphone attribué');
    } catch (error: any) {
      if (error.response?.status === 404) {
        ToastError(
          'Erreur',
          "Il n'y a plus de numéros TELNYX disponibles pour le moment.",
        );
      } else {
        ToastError('Erreur', "Impossible d'attribuer les numéros TELNYX");
      }
      throw error;
    }
  }, [proViewId, buyPhoneNumber, refetchPhoneNumbers]);

  const onReplacePhoneNumber = useCallback(
    async (phoneNumberId: number, oldNumber: string): Promise<void> => {
      try {
        await replacePhoneNumber({
          path: { phoneNumberId },
        });

        await refetchPhoneNumbers();
        ToastSuccess('Succès', `Numéro remplacé: ${oldNumber}`);
      } catch (error: any) {
        ToastError(
          'Erreur',
          `Impossible de remplacer le numéro: ${
            error.response?.data?.message || error.message
          }`,
        );
        throw error;
      }
    },
    [replacePhoneNumber, refetchPhoneNumbers],
  );

  const { data: webPhoneNumberStatus } = useQuery({
    ...getPhoneNumberStatusOptions({
      path: { phoneNumberId: webPhoneNumber?.id ?? 0 },
    }),
    enabled: !!webPhoneNumber?.id,
  });

  const { data: guidePhoneNumberStatus } = useQuery({
    ...getPhoneNumberStatusOptions({
      path: { phoneNumberId: guidePhoneNumber?.id ?? 0 },
    }),
    enabled: !!guidePhoneNumber?.id,
  });

  return (
    <>
      {values.status !== 'archived' &&
      (!values.phoneNumbers || values.phoneNumbers.length === 0) ? (
        <>
          <ConfirmModal
            trigger={
              <Button
                type="button"
                className="warning"
                compact
                onClick={onBuyProNumber}
              >
                Générer
              </Button>
            }
            header="Confirmation"
            content={
              <p>Cette action va acheter un nouveau numéro. Continuer ?</p>
            }
            onConfirm={onBuyProNumber}
          />
        </>
      ) : null}

      {values.phoneNumbers?.length ? (
        <FormField>
          {webPhoneNumber && (
            <div
              style={{
                display: 'flex',
                fontWeight: 'bold',
                marginBottom: '4px',
              }}
            >
              <span style={{ width: '100px', display: 'inline-block' }}>
                Web
              </span>
              <LabelUi key={`call-tracking-number-web`} size="big" style={{}}>
                {PhoneNumber.format(webPhoneNumber.number)}
              </LabelUi>
              {renderPhoneNumberStatusLabel(
                webPhoneNumberStatus ?? 'unverified',
              )}
              {values.status !== 'archived' && webPhoneNumber.id && (
                <ReplacePhoneNumberButton
                  phoneNumberId={webPhoneNumber.id}
                  phoneNumber={webPhoneNumber.number}
                  onReplace={onReplacePhoneNumber}
                />
              )}
            </div>
          )}

          {guidePhoneNumber && (
            <div
              style={{
                display: 'flex',
                fontWeight: 'bold',
              }}
            >
              <span style={{ width: '100px', display: 'inline-block' }}>
                Guide
              </span>
              <LabelUi
                key={`call-tracking-number-guide`}
                size="big"
                style={{ display: 'inline-block' }}
              >
                {PhoneNumber.format(guidePhoneNumber.number)}
              </LabelUi>
              {renderPhoneNumberStatusLabel(
                guidePhoneNumberStatus ?? 'unverified',
              )}
              {values.status !== 'archived' && guidePhoneNumber.id && (
                <ReplacePhoneNumberButton
                  phoneNumberId={guidePhoneNumber.id}
                  phoneNumber={guidePhoneNumber.number}
                  onReplace={onReplacePhoneNumber}
                />
              )}
            </div>
          )}

          {otherPhoneNumbers.length ? (
            <Label>
              <span style={{ width: '100px', display: 'inline-block' }}>
                Autre(s)
              </span>
              {otherPhoneNumbers.map((phoneNumber) => (
                <Popup
                  key={`call-tracking-number-${phoneNumber.number}`}
                  content={phoneNumber.tag}
                  trigger={
                    <LabelUi size="big" style={{ display: 'inline-block' }}>
                      {PhoneNumber.format(phoneNumber.number)}
                    </LabelUi>
                  }
                />
              ))}
            </Label>
          ) : null}

          {releasedPhoneNumbers.length > 0 && (
            <Label>
              <span
                style={{
                  color: '#DB2828',
                  width: '100px',
                  display: 'inline-block',
                }}
              >
                Supprimé(s){' '}
                <Popup
                  content="Numéros supprimés non fonctionnels conservés en base de données pour éviter qu'ils soient réattribués à d'autres professionnels."
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ cursor: 'pointer', color: '#DB2828' }}
                    />
                  }
                />
              </span>
              {releasedPhoneNumbers.map((phoneNumber) => (
                <Popup
                  key={`call-tracking-number-${phoneNumber.number}`}
                  content={phoneNumber.tag}
                  trigger={
                    <LabelUi size="big" style={{ display: 'inline-block' }}>
                      {PhoneNumber.format(phoneNumber.number)}
                    </LabelUi>
                  }
                />
              ))}
            </Label>
          )}
        </FormField>
      ) : null}

      {errors.phoneNumbers ? (
        <div>
          <FieldError color="red">{errors.phoneNumbers}</FieldError>
        </div>
      ) : null}
      <div style={{ height: '15px' }} />
    </>
  );
};

export default ProPhoneNumbersField;
